import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';

import { loadPipeline, loadPipelineCatalogue } from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, SummaryBar, SummaryItem, XomicsSelect,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { statusLabel } from './labels';

const NUMBER = new Intl.NumberFormat();

function duration(minutes) {
  const value = Number(minutes || 0);
  if (value < 60) return tx('ui.common.minutes_short', { count: value });
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest === 0
    ? tx('ui.common.hours_short', { count: hours })
    : tx('ui.common.hours_minutes_short', { hours, minutes: rest });
}

function elapsed(milliseconds) {
  if (milliseconds === null || milliseconds === undefined) return '—';
  return duration(Math.max(1, Math.round(Number(milliseconds) / 60000)));
}

function when(value) {
  if (!value) return tx('ui.common.never');
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function PipelineRow({ pipeline, onOpen }) {
  const output = pipeline.outputSummary;
  return (
    <button type="button" className="xo-pipeline-row" onClick={onOpen}>
      <span className="xo-pipeline-row-main">
        <span className="xo-lines">
          <span className="xo-pipeline-row-title">
            <span className="xo-pipeline-omics">{pipeline.omics}</span>
            <strong>{pipeline.displayName}</strong>
          </span>
          <span className="xo-pipeline-version">{pipeline.key} · v{pipeline.codeVersion}</span>
          <span>{pipeline.description || tx("ui.pipelines.no_description_supplied_f33babd7")}</span>
        </span>
      </span>

      <span className="xo-pipeline-flags">
        <Pill tone={pipeline.status === 'online' ? 'ok' : 'flat'}>{statusLabel(pipeline.status)}</Pill>
        <Pill tone={pipeline.isPatient ? 'info' : 'flat'}>{pipeline.isPatient ? tx("ui.pipelines.patient_de5c57ff") : tx("ui.pipelines.cohort_external_8d852d23")}</Pill>
      </span>

      <span className="xo-pipeline-cell">
        <strong>{tx("ui.pipelines.length_inputvalue_files_outputs_d314a16c", { length: pipeline.inputs.length, value: pipeline.inputs.length === 1 ? '' : 's', files: output.files })}</strong>
        <small>{tx("ui.pipelines.importablefiles_importable_value_declared_columns_08dc5e2f", { importableFiles: output.importableFiles, value: NUMBER.format(output.columns) })}</small>
      </span>
      <ChevronRight className="xo-pipeline-chevron" aria-hidden="true" />
    </button>
  );
}

function Facts({ pipeline }) {
  const facts = [
    [tx('ui.pipelines.fact_identifier'), pipeline.key],
    [tx('ui.pipelines.fact_code_directory'), pipeline.codeDir || '—'],
    [tx('ui.pipelines.fact_image'), pipeline.imageTag],
    [tx('ui.pipelines.fact_omics'), pipeline.omics],
    [tx('ui.pipelines.fact_patient_scope'), pipeline.isPatient
      ? tx('ui.pipelines.single_patient') : tx('ui.pipelines.cohort_or_external')],
    [tx('ui.pipelines.fact_repeatability'), pipeline.isRepeatable
      ? tx('ui.pipelines.repeatable') : tx('ui.pipelines.one_run_per_sample')],
    [tx('ui.pipelines.fact_created'), when(pipeline.createdAt)],
    [tx('ui.pipelines.fact_updated'), when(pipeline.updatedAt)],
  ];
  return (
    <dl className="xo-pipeline-facts">
      {facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>
  );
}

function Inputs({ pipeline }) {
  const patientFields = Object.entries(pipeline.patientFields || {});
  return (
    <section className="xo-pipeline-detail-section">
      <div className="xo-pipeline-section-head"><div><h3>{tx("ui.pipelines.input_contract_19982624")}</h3><p>{tx("ui.pipelines.exact_slots_presented_to_an_upload_and_mounted_for_the_4dec4a74")}</p></div></div>
      {pipeline.inputs.length === 0 ? <p className="xo-muted">{tx("ui.pipelines.no_input_slots_declared_9cc19ad5")}</p> : (
        <div className="xo-table-wrap">
          <table className="xo-table">
            <thead><tr><th>#</th><th>{tx("ui.pipelines.input_b568d47f")}</th><th>{tx("ui.pipelines.meaning_19cd7d18")}</th><th>{tx("ui.pipelines.format_041a5dec")}</th><th>{tx("ui.pipelines.requirement_e9c366b4")}</th><th>{tx("ui.pipelines.multiplicity_3a9a991a")}</th><th>{tx("ui.pipelines.notes_70440046")}</th></tr></thead>
            <tbody>{pipeline.inputs.map((input) => (
              <tr key={input.id}>
                <td className="xo-num">{input.position}</td><td><strong>{input.title}</strong></td>
                <td><span className="xo-mono">{input.contentKind}</span><small className="xo-cell-note">{input.contentKindLabel}</small></td>
                <td><Pill tone="flat">{input.fileFormat}</Pill></td>
                <td>{input.required ? tx("ui.pipelines.required_eed6bfb4") : tx("ui.pipelines.optional_0c6c4102")}</td>
                <td>{input.acceptsMultiple ? tx("ui.pipelines.one_or_more_files_d1582a78") : tx("ui.pipelines.one_file_c1ba4c5e")}</td>
                <td>{input.notes || <span className="xo-muted">—</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <div className="xo-pipeline-contract-extras">
        <div><h4>{tx("ui.pipelines.accepted_sample_types_56e86ec3")}</h4>
          <div className="xo-chips">{pipeline.sampleTypes.length === 0
            ? <span className="xo-muted">{tx("ui.pipelines.none_declared_b2e591f4")}</span>
            : pipeline.sampleTypes.map((sample) => <span className="xo-chip" key={sample.code} title={sample.description}>{sample.code} · {sample.name}</span>)}</div>
        </div>
        <div><h4>{tx("ui.pipelines.patient_values_passed_as_parameters_c7730c08")}</h4>
          <div className="xo-chips">{patientFields.length === 0
            ? <span className="xo-muted">{tx("ui.pipelines.none_6eef6648")}</span>
            : patientFields.map(([field, parameter]) => <span className="xo-chip" key={field}>{field} → {parameter}</span>)}</div>
        </div>
        <div><h4>{tx("ui.pipelines.immutable_reference_sets_4c845453")}</h4>
          <div className="xo-chips">{pipeline.references.length === 0
            ? <span className="xo-muted">{tx("ui.pipelines.none_6eef6648")}</span>
            : pipeline.references.map((reference) => <span className="xo-chip" key={reference}>{reference}</span>)}</div>
        </div>
      </div>
    </section>
  );
}

function OutputArtifact({ artifact, expanded, onToggle }) {
  return (
    <article className={`xo-output-artifact${expanded ? ' is-open' : ''}`}>
      <button type="button" className="xo-output-artifact-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="xo-output-position">{artifact.position}</span>
        <span className="xo-lines">
          <strong className="xo-mono">{artifact.filePattern}</strong>
          <span>
            {artifact.set} · {artifact.fileFormat} · {artifact.required ? tx("ui.pipelines.required_1a77d416") : tx("ui.pipelines.optional_48a7b888")}
            {artifact.note ? tx("ui.pipelines.note_691a839b", { note: artifact.note }) : ''}
          </span>
        </span>
        <span className="xo-output-target">
          <Pill tone={artifact.importable ? 'ok' : 'flat'}>{artifact.importable ? tx("ui.pipelines.importable_7a0d33ac") : tx("ui.pipelines.retained_only_0b51614f")}</Pill>
          {artifact.targetTable && <span className="xo-mono">{artifact.targetTable}</span>}
        </span>
        <span className="xo-output-column-count">{tx("ui.pipelines.length_columns_2a84702d", { length: artifact.columns.length })}</span>
        <ChevronRight aria-hidden="true" />
      </button>
      {expanded && (
        <div className="xo-output-columns">
          {artifact.columns.length === 0 ? (
            <p className="xo-muted">{tx("ui.pipelines.this_native_artifact_has_no_relational_column_contract_75b836a1")}</p>
          ) : (
            <table className="xo-table">
              <thead><tr><th>#</th><th>{tx("ui.pipelines.source_file_header_853aebb3")}</th><th>{tx("ui.pipelines.target_column_b9ce404b")}</th><th>{tx("ui.pipelines.type_3deb7456")}</th><th>{tx("ui.pipelines.nulls_3d1fa263")}</th></tr></thead>
              <tbody>{artifact.columns.map((column) => (
                <tr key={`${artifact.id}-${column.position}`}>
                  <td className="xo-num">{column.position}</td>
                  <td className="xo-mono">{column.sourceName === '' ? <em>{tx("ui.pipelines.unnamed_column_c5f06de9")}</em> : column.sourceName}</td>
                  <td className="xo-mono">{column.targetName}</td>
                  <td><Pill tone="flat">{column.dataType}</Pill></td>
                  <td>{column.nullable ? tx("ui.pipelines.nullable_1804c908") : tx("ui.pipelines.required_eed6bfb4")}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </article>
  );
}

function Outputs({ pipeline }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const needle = query.trim().toLowerCase();
  const artifacts = useMemo(() => pipeline.artifacts.filter((artifact) => {
    if (needle === '') return true;
    return [artifact.filePattern, artifact.targetTable, artifact.fileFormat, artifact.note]
      .some((value) => value && String(value).toLowerCase().includes(needle))
      || artifact.columns.some((column) => [column.sourceName, column.targetName, column.dataType]
        .some((value) => value && String(value).toLowerCase().includes(needle)));
  }), [pipeline.artifacts, needle]);

  function toggle(id) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const shownColumns = artifacts.reduce((sum, artifact) => sum + artifact.columns.length, 0);
  return (
    <section className="xo-pipeline-detail-section">
      <div className="xo-pipeline-section-head">
        <div><h3>{tx("ui.pipelines.output_contract_9bdd8bd2")}</h3><p>{tx("ui.pipelines.every_retained_result_and_its_exact_relational_mapping_65ba104e")}</p></div>
        <div className="xo-bar-group">
          <SearchField value={query} onChange={setQuery}
            placeholder={tx("ui.pipelines.find_file_table_or_column_45ebccee")}
            label={tx("ui.pipelines.search_output_contract_2769f334")} />
          <button type="button" className="xo-btn-2" onClick={() => {
            const allOpen = artifacts.every((artifact) => expanded.has(artifact.id));
            setExpanded(allOpen ? new Set() : new Set(artifacts.map((artifact) => artifact.id)));
          }}>{artifacts.every((artifact) => expanded.has(artifact.id)) ? tx("ui.pipelines.collapse_all_ec898362") : tx("ui.pipelines.show_all_columns_4c49c058")}</button>
        </div>
      </div>
      <p className="xo-output-summary">{tx("ui.pipelines.length_of_length2_artifacts_value_columns_value2_impor_42fbf883", { length: artifacts.length, length2: pipeline.artifacts.length, value: NUMBER.format(shownColumns), value2: ' ', importableFiles: pipeline.outputSummary.importableFiles, newSchemaFiles: pipeline.outputSummary.newSchemaFiles, value3: ' ', baseFiles: pipeline.outputSummary.baseFiles })}</p>
      <div className="xo-output-list">
        {artifacts.map((artifact) => <OutputArtifact key={artifact.id} artifact={artifact}
          expanded={expanded.has(artifact.id)} onToggle={() => toggle(artifact.id)} />)}
      </div>
    </section>
  );
}

function RunsAndReleases({ pipeline }) {
  const runFacts = [
    [tx('ui.pipelines.run_total'), pipeline.runs.total],
    [tx('ui.pipelines.run_complete'), pipeline.runs.complete],
    [tx('ui.pipelines.run_failed'), pipeline.runs.failed],
    [tx('ui.pipelines.run_running'), pipeline.runs.running],
    [tx('ui.pipelines.run_queued'), pipeline.runs.queued],
    [tx('ui.pipelines.run_average_runtime'), elapsed(pipeline.runs.averageRunMs)],
  ];
  return (
    <section className="xo-pipeline-detail-section">
      <div className="xo-pipeline-section-head"><div><h3>{tx("ui.pipelines.use_and_release_history_1cbfe34f")}</h3><p>{tx("ui.pipelines.the_pipeline_row_stays_pinned_to_every_release_and_job_47441761")}</p></div></div>
      <div className="xo-pipeline-history-grid">
        <div className="xo-run-facts">{runFacts.map(([label, value]) => <span key={label}><small>{label}</small><strong>{value}</strong></span>)}</div>
        <div className="xo-release-history">
          {pipeline.releases.map((release) => (
            <span key={release.id}><Pill tone={release.status === 'active' ? 'ok' : 'flat'}>{statusLabel(release.status)}</Pill>
              <strong>{release.tag}</strong><small>{String(release.gitCommit).slice(0, 12)} · {when(release.createdAt)}</small></span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PipelineDetail({ id, onClose }) {
  const load = useCallback(() => loadPipeline(id), [id]);
  const resource = useLiveResource(['pipelines'], load);

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  return (
    <div className="xo-pipeline-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="xo-pipeline-modal" role="dialog" aria-modal="true" aria-labelledby="pipeline-detail-title">
        {resource.loading && resource.data === null ? <Loading label={tx("ui.pipelines.reading_the_complete_pipeline_contract_ee3912e6")} />
          : resource.error && resource.data === null ? <Failed message={resource.error} onRetry={resource.refresh} />
            : (() => {
              const pipeline = resource.data;
              return (
                <>
                  <header className="xo-pipeline-modal-head">
                    <div>
                      <span className="xo-pipeline-modal-flags">
                        <Pill tone={pipeline.inActiveRelease ? 'ok' : 'flat'}>{pipeline.inActiveRelease ? tx("ui.pipelines.active_release_7ccf31b8") : statusLabel(pipeline.status)}</Pill>
                        <Pill tone={pipeline.isPatient ? 'info' : 'flat'}>{pipeline.isPatient ? tx("ui.pipelines.patient_de5c57ff") : tx("ui.pipelines.cohort_external_8d852d23")}</Pill>
                      </span>
                      <h2 id="pipeline-detail-title">{pipeline.displayName}</h2>
                      <p><span className="xo-mono">{pipeline.key}</span> · v{pipeline.codeVersion}</p>
                    </div>
                    <button type="button" className="xo-btn-icon" aria-label={tx("ui.pipelines.close_pipeline_51d44574")} onClick={onClose}><X aria-hidden="true" /></button>
                  </header>
                  <div className="xo-pipeline-modal-scroll">
                    {resource.error && <p className="xo-alert t-warn">{resource.error}</p>}
                    <p className="xo-pipeline-description">{pipeline.description || tx("ui.pipelines.no_description_supplied_f33babd7")}</p>
                    {pipeline.updateNotes && <div className="xo-pipeline-update"><strong>{tx("ui.pipelines.what_changed_in_vcodeversion_d9a7e085", { codeVersion: pipeline.codeVersion })}</strong><p>{pipeline.updateNotes}</p></div>}
                    <Facts pipeline={pipeline} />
                    <Inputs pipeline={pipeline} />
                    <Outputs pipeline={pipeline} />
                    <RunsAndReleases pipeline={pipeline} />
                  </div>
                </>
              );
            })()}
      </section>
    </div>
  );
}

export default function XomicsPipelines() {
  const [scope, setScope] = useState('active');
  const [query, setQuery] = useState('');
  const [patientScope, setPatientScope] = useState('all');
  const [omics, setOmics] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  // ?pipeline=<id> opens that pipeline, which is how quick search links here.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPipeline = Number(searchParams.get('pipeline'));
  useEffect(() => {
    if (Number.isSafeInteger(linkedPipeline) && linkedPipeline > 0) setSelectedId(linkedPipeline);
  }, [linkedPipeline]);
  function closePipeline() {
    setSelectedId(null);
    if (searchParams.get('pipeline') === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete('pipeline');
    setSearchParams(next, { replace: true });
  }
  const load = useCallback(() => loadPipelineCatalogue({ scope }), [scope]);
  const catalogue = useLiveResource(['pipelines'], load);
  const data = catalogue.data || { activeRelease: null, pipelines: [] };
  const omicsOptions = useMemo(() => [...new Set(data.pipelines.map((pipeline) => pipeline.omics))].sort(), [data.pipelines]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return data.pipelines.filter((pipeline) => {
      if (patientScope === 'patient' && !pipeline.isPatient) return false;
      if (patientScope === 'external' && pipeline.isPatient) return false;
      if (omics !== 'all' && pipeline.omics !== omics) return false;
      if (needle === '') return true;
      return [pipeline.displayName, pipeline.name, pipeline.mode, pipeline.codeVersion,
        pipeline.description, pipeline.updateNotes, pipeline.imageTag, ...pipeline.references]
        .some((value) => value && String(value).toLowerCase().includes(needle));
    });
  }, [data.pipelines, query, patientScope, omics]);

  if (catalogue.loading && catalogue.data === null) return <Loading label={tx("ui.pipelines.reading_pipeline_catalogue_1a98e60a")} />;
  if (catalogue.error && catalogue.data === null) return <Failed message={catalogue.error} onRetry={catalogue.refresh} />;

  const totalFiles = data.pipelines.reduce((sum, pipeline) => sum + pipeline.outputSummary.files, 0);
  const totalColumns = data.pipelines.reduce((sum, pipeline) => sum + pipeline.outputSummary.columns, 0);
  const patientCount = data.pipelines.filter((pipeline) => pipeline.isPatient).length;

  return (
    <div className="xo-platform-page">
      <Head title={tx("ui.pipelines.pipelines_c5df1e14")} subtitle={data.activeRelease
        ? tx("ui.pipelines.tag_value_a99fc4e4", { tag: data.activeRelease.tag, value: String(data.activeRelease.gitCommit).slice(0, 12) })
        : tx("ui.pipelines.no_active_release_d2e250ab")} />
      {catalogue.error && <p className="xo-alert t-warn">{catalogue.error}</p>}
      <SummaryBar>
        <SummaryItem value={data.pipelines.length} label={scope === 'active' ? tx("ui.pipelines.active_pipelines_18223af7") : tx("ui.pipelines.pipeline_versions_031921bf")}
          detail={scope === 'active' ? tx("ui.pipelines.patientcount_patient_value_cohort_external_fc64b947", { patientCount: patientCount, value: data.pipelines.length - patientCount }) : tx("ui.pipelines.includes_superseded_versions_898bded8")} />
        <SummaryItem value={data.pipelines.reduce((sum, pipeline) => sum + pipeline.inputs.length, 0)} label={tx("ui.pipelines.input_slots_df1c1824")}
          detail={tx("ui.pipelines.semantic_file_roles_ba4cc4a2")} />
        <SummaryItem value={NUMBER.format(totalFiles)} label={tx("ui.pipelines.declared_outputs_c95a85d3")}
          detail={tx("ui.pipelines.value_marked_importable_eb86e414", { value: data.pipelines.reduce((sum, pipeline) => sum + pipeline.outputSummary.importableFiles, 0) })} />
        <SummaryItem value={NUMBER.format(totalColumns)} label={tx("ui.pipelines.output_columns_0d406cc8")}
          detail={tx("ui.pipelines.exact_source_to_target_mappings_1fecd601")} />
      </SummaryBar>

      <div className="xo-pipeline-toolbar">
        <SearchField value={query} onChange={setQuery}
          placeholder={tx("ui.pipelines.search_name_mode_version_image_or_reference_22e292c0")}
          label={tx("ui.pipelines.search_pipelines_661b1633")} />
        <div className="xo-pipeline-filter-groups">
          <div className="xo-seg" role="group" aria-label={tx("ui.pipelines.pipeline_versions_6c164c52")}>
            <button type="button" className={scope === 'active' ? 'is-on' : ''} onClick={() => setScope('active')}>{tx("ui.pipelines.active_release_7ccf31b8")}</button>
            <button type="button" className={scope === 'all' ? 'is-on' : ''} onClick={() => setScope('all')}>{tx("ui.pipelines.all_versions_63af3b74")}</button>
          </div>
          <XomicsSelect value={patientScope} aria-label={tx("ui.pipelines.patient_scope_b9b69c46")} onChange={(event) => setPatientScope(event.target.value)}>
            <option value="all">{tx("ui.pipelines.all_scopes_b260408b")}</option><option value="patient">{tx("ui.pipelines.patient_pipelines_8be72d88")}</option><option value="external">{tx("ui.pipelines.cohort_external_8d852d23")}</option>
          </XomicsSelect>
          <XomicsSelect value={omics} aria-label={tx("ui.pipelines.omics_type_6d0f492f")} onChange={(event) => setOmics(event.target.value)}>
            <option value="all">{tx("ui.pipelines.all_omics_dedda4cf")}</option>{omicsOptions.map((value) => <option key={value} value={value}>{value}</option>)}
          </XomicsSelect>
        </div>
      </div>

      <div className="xo-data-surface xo-pipeline-list">
        {shown.length === 0 ? <Empty title={tx("ui.pipelines.no_pipelines_match_4b1af251")} />
          : shown.map((pipeline) => <PipelineRow key={pipeline.id} pipeline={pipeline} onOpen={() => setSelectedId(pipeline.id)} />)}
      </div>
      <p className="xo-list-foot">{tx("ui.pipelines.showing_length_of_length2_pipeline_versions_9e7273dc", { length: shown.length, length2: data.pipelines.length })}</p>
      {selectedId !== null && <PipelineDetail id={selectedId} onClose={closePipeline} />}
    </div>
  );
}
